/**
 * Utility functions for generating Orin's automated insights
 * from opportunity assessment data
 */

import { createTLSchema } from '@tldraw/tldraw'

/**
 * Parse the assessment reasoning to extract structured sections
 */
export function parseAssessmentSections(reasoning) {
  if (!reasoning) {
    console.log('parseAssessmentSections: no reasoning provided')
    return null
  }

  console.log('parseAssessmentSections: parsing reasoning of length', reasoning.length)
  console.log('parseAssessmentSections: first 1000 chars:', reasoning.substring(0, 1000))
  console.log('parseAssessmentSections: full reason:', reasoning)

  // Extract sections - handle multiple formats:
  // 1. Markdown format: **🚀 Why This Could Work:**
  // 2. Plain text format: 🚀\nWhy This Could Work
  // 3. With or without colons

  // Try markdown format first (with ** wrapper)
  let summaryMatch = reasoning.match(/\*\*SUMMARY:\*\*\s*([\s\S]*?)(?=\n\n\*\*|🚀|⚠️|🎯|$)/i)
  let whyWorkMatch = reasoning.match(/\*\*(?:🚀\s*)?Why This Could Work:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|⚠️|🎯|$)/i)
  let holdBackMatch = reasoning.match(/\*\*(?:⚠️\s*)?What Could Hold It Back:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|🎯|$)/i)
  let nextStepsMatch = reasoning.match(/\*\*(?:🎯\s*)?Next Steps(?: to Consider)?:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|$)/i)

  // Try plain text format (emoji on own line or inline, no ** wrapper)
  if (!whyWorkMatch) {
    whyWorkMatch = reasoning.match(/🚀\s*\n?\s*Why This Could Work\s*\n([\s\S]*?)(?=\n\n?⚠️|$)/i)
  }
  if (!holdBackMatch) {
    holdBackMatch = reasoning.match(/⚠️\s*\n?\s*What Could Hold It Back\s*\n([\s\S]*?)(?=\n\n?🎯|$)/i)
  }
  if (!nextStepsMatch) {
    nextStepsMatch = reasoning.match(/🎯\s*\n?\s*Next Steps(?: to Consider)?\s*\n([\s\S]*?)$/i)
  }
  if (!summaryMatch) {
    summaryMatch = reasoning.match(/SUMMARY:?\s*([\s\S]*?)(?=\n\n?🚀|$)/i)
  }

  // Legacy format fallback
  const prosMatch = reasoning.match(/\*\*(?:✅\s*)?Pros:\*\*\s*([\s\S]*?)(?=\n\n\*\*|$)/i)
  const consMatch = reasoning.match(/\*\*(?:⚠️\s*)?Cons:\*\*\s*([\s\S]*?)(?=\n\n\*\*|$)/i)
  const recommendationsMatch = reasoning.match(/\*\*(?:💡\s*)?Recommendations:\*\*\s*([\s\S]*?)(?=\n\n\*\*|$)/i)

  console.log('parseAssessmentSections: matches found:', {
    summary: !!summaryMatch,
    whyWork: !!whyWorkMatch,
    holdBack: !!holdBackMatch,
    nextSteps: !!nextStepsMatch,
    pros: !!prosMatch,
    cons: !!consMatch,
    recommendations: !!recommendationsMatch
  })

  const parseSection = (content) => {
    if (!content) return null
    const lines = content.trim().split('\n').filter(line => line.trim())
    // Match bullets starting with -, •, ▸, or numbered items
    const bulletPattern = /^[-•▸]\s*|^\d+\.\s*/
    const bullets = lines.filter(line => bulletPattern.test(line.trim()))
    const summaryLines = lines.filter(line => !bulletPattern.test(line.trim()))
    return {
      summary: summaryLines.join(' ').trim(),
      bullets: bullets.map(b => b.trim().replace(/^[-•▸]\s*/, '').replace(/^\d+\.\s*/, ''))
    }
  }

  // Determine format and extract sections
  const isNewFormat = whyWorkMatch && holdBackMatch

  const result = {
    summary: summaryMatch ? summaryMatch[1].trim() : null,
    strengths: parseSection(isNewFormat ? whyWorkMatch?.[1] : prosMatch?.[1]),
    weaknesses: parseSection(isNewFormat ? holdBackMatch?.[1] : consMatch?.[1]),
    nextSteps: parseSection(isNewFormat ? nextStepsMatch?.[1] : recommendationsMatch?.[1]),
    isNewFormat
  }

  console.log('parseAssessmentSections: result:', {
    hasSummary: !!result.summary,
    hasStrengths: !!result.strengths,
    strengthBullets: result.strengths?.bullets?.length || 0,
    hasWeaknesses: !!result.weaknesses,
    weaknessBullets: result.weaknesses?.bullets?.length || 0,
    hasNextSteps: !!result.nextSteps,
    nextStepsBullets: result.nextSteps?.bullets?.length || 0,
    isNewFormat: result.isNewFormat
  })

  return result
}

/**
 * Generate SWOT-style whiteboard snapshot from assessment sections, USP evaluation, and market demand
 * Always creates a NEW page with timestamp - doesn't check for or replace existing SWOT pages.
 * Caller is responsible for merging the returned data with existing whiteboard content.
 * @param {Object} sections - Parsed assessment sections (strengths, weaknesses, nextSteps)
 * @param {Object} uspSections - Parsed USP sections (differentiators, weakDifferentiation, strengthenUniqueness)
 * @param {Object} marketDemandSections - Parsed market demand sections (promising, uncertain, validate)
 * @param {Object} existingData - Existing whiteboard data (used for reference, not modified)
 * @returns {Object|null} - New SWOT page data to be merged, or null if no content to generate
 */
export function generateSwotWhiteboard(sections, uspSections = null, marketDemandSections = null, existingData = null) {
  console.log('generateSwotWhiteboard: called with sections:', sections, 'uspSections:', uspSections, 'marketDemandSections:', marketDemandSections)

  // Always create a new SWOT page - each generation gets a unique timestamp
  // This allows multiple SWOT analyses to coexist as separate pages

  const { strengths, weaknesses } = sections || {}
  const { differentiators, weakDifferentiation } = uspSections || {}
  const { promising } = marketDemandSections || {}

  // Check if we have any content to generate (from assessment, USP, or market demand)
  const hasAssessmentContent = strengths?.bullets?.length || weaknesses?.bullets?.length
  const hasUSPContent = differentiators?.bullets?.length || weakDifferentiation?.bullets?.length
  const hasMarketDemandContent = promising?.bullets?.length

  if (!hasAssessmentContent && !hasUSPContent && !hasMarketDemandContent) {
    console.log('generateSwotWhiteboard: no bullets found in any section, cannot generate whiteboard')
    return null
  }

  console.log('generateSwotWhiteboard: generating with bullets:', {
    strengths: strengths?.bullets?.length || 0,
    weaknesses: weaknesses?.bullets?.length || 0,
    differentiators: differentiators?.bullets?.length || 0,
    weakDifferentiation: weakDifferentiation?.bullets?.length || 0,
    promising: promising?.bullets?.length || 0
  })

  // Create a visual SWOT-style layout with center quadrant and surrounding notes
  const shapes = {}

  // Helper to create a unique ID
  const createId = () => `shape:${Math.random().toString(36).substr(2, 9)}`

  // Helper to convert plain text to tldraw richText format (TipTap JSON)
  const toRichText = (text) => ({
    type: 'doc',
    content: text.split('\n').map(line =>
      line ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' }
    )
  })

  let shapeIndex = 1

  // Geo shape for center quadrant boxes (colored rectangles with labels)
  const createGeoShape = (id, x, y, w, h, text, color) => ({
    id,
    typeName: 'shape',
    type: 'geo',
    x,
    y,
    rotation: 0,
    isLocked: false,
    opacity: 1,
    meta: {},
    props: {
      geo: 'rectangle',
      w,
      h,
      color,
      labelColor: 'white',
      fill: 'solid',
      dash: 'draw',
      size: 'm',
      font: 'sans',
      richText: toRichText(text),
      align: 'middle',
      verticalAlign: 'middle',
      growY: 0,
      url: '',
      scale: 1
    },
    parentId: 'page:page',
    index: `a${shapeIndex++}`
  })

  // Note shape for sticky note cards
  const createNoteShape = (id, x, y, text, color = 'grey') => ({
    id,
    typeName: 'shape',
    type: 'note',
    x,
    y,
    rotation: (Math.random() - 0.5) * 0.1, // Slight random rotation for natural look
    isLocked: false,
    opacity: 1,
    meta: {},
    props: {
      richText: toRichText(text),
      color,
      labelColor: 'white',
      size: 's',
      font: 'sans',
      align: 'middle',
      verticalAlign: 'middle',
      growY: 0,
      fontSizeAdjustment: 0,
      url: '',
      scale: 1
    },
    parentId: 'page:page',
    index: `a${shapeIndex++}`
  })

  // 4-COLUMN LAYOUT: Headers at top, notes stacked below in columns
  // Columns: Strengths | Weaknesses | Opportunities | Threats

  const headerWidth = 200  // Width of header geo boxes (fits "Opportunities")
  const headerHeight = 50  // Height of header geo boxes
  const columnWidth = 200  // Width of each column (increased for better spacing)
  const columnGap = 30  // Gap between columns (increased)
  const startX = 100  // Left edge of first column
  const headerY = 50  // Y position of headers
  const notesStartY = headerY + headerHeight + 40  // Notes start below headers
  const noteSpacingY = 280  // Vertical spacing between notes (sized for medium notes with ~120 char text)

  // Helper to truncate text at word boundary
  const truncateText = (text, maxLength = 120) => {
    if (text.length <= maxLength) return text
    const truncated = text.slice(0, maxLength)
    const lastSpace = truncated.lastIndexOf(' ')
    return lastSpace > maxLength * 0.5 ? truncated.slice(0, lastSpace) + '...' : truncated + '...'
  }

  // Column definitions with their data and colors
  // Strengths: assessment strengths + USP differentiators
  // Weaknesses: USP weak differentiation points
  // Opportunities: market demand "What's Promising" (positive market signals)
  // Threats: assessment weaknesses (market/execution challenges)
  const strengthsBullets = [
    ...(strengths?.bullets || []),
    ...(differentiators?.bullets || [])
  ]
  const weaknessesBullets = weakDifferentiation?.bullets || []
  const opportunitiesBullets = promising?.bullets || []
  const threatsBullets = weaknesses?.bullets || []

  const columns = [
    { label: 'Strengths', headerColor: 'green', noteColor: 'light-green', bullets: strengthsBullets },
    { label: 'Weaknesses', headerColor: 'orange', noteColor: 'yellow', bullets: weaknessesBullets },
    { label: 'Opportunities', headerColor: 'blue', noteColor: 'light-blue', bullets: opportunitiesBullets },
    { label: 'Threats', headerColor: 'red', noteColor: 'light-red', bullets: threatsBullets },
  ]

  columns.forEach((col, colIdx) => {
    const colX = startX + colIdx * (columnWidth + columnGap)

    // Create header geo box
    const headerId = createId()
    shapes[headerId] = createGeoShape(headerId, colX, headerY, headerWidth, headerHeight, col.label, col.headerColor)

    // Create notes stacked below header
    col.bullets.slice(0, 4).forEach((bullet, noteIdx) => {
      const noteId = createId()
      const noteX = colX - 20  // Slight offset to center under header
      const noteY = notesStartY + noteIdx * noteSpacingY
      shapes[noteId] = createNoteShape(noteId, noteX, noteY, truncateText(bullet), col.noteColor)
    })
  })

  // Generate page name with date/time
  const now = new Date()
  const dateStr = now.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
  const timeStr = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  })
  const swotPageName = `SWOT ${dateStr} ${timeStr}`
  const swotPageId = `page:swot_${Date.now()}`

  // Build store entries for just the new SWOT page and its shapes
  // Does NOT include document:document - caller merges with existing document
  // Page index uses timestamp to ensure unique ordering after existing pages
  const pageIndex = `b${Date.now()}` // 'b' prefix ensures it sorts after 'a' prefixed pages

  const newStoreEntries = {
    [swotPageId]: {
      id: swotPageId,
      typeName: 'page',
      name: swotPageName,
      index: pageIndex,
      meta: {}
    }
  }

  // Add SWOT shapes parented to the SWOT page
  Object.entries(shapes).forEach(([key, shape]) => {
    newStoreEntries[key] = {
      ...shape,
      parentId: swotPageId,
      meta: {}
    }
  })

  console.log('generateSwotWhiteboard: created SWOT page:', swotPageName, 'with', Object.keys(shapes).length, 'shapes')

  // Return the new entries to be merged into existing store
  return { newStoreEntries, pageId: swotPageId, pageName: swotPageName }
}

/**
 * Generate a short summary title from a longer task description
 */
function generateTaskTitle(fullText) {
  // Find a good break point for a summary (first sentence or phrase)
  const maxLength = 80

  // Try to find a natural break point
  const breakpoints = ['. ', ' - ', ': ', ' (']
  let title = fullText

  for (const bp of breakpoints) {
    const idx = fullText.indexOf(bp)
    if (idx > 0 && idx < maxLength) {
      title = fullText.slice(0, idx)
      break
    }
  }

  // If still too long, truncate at word boundary
  if (title.length > maxLength) {
    title = title.slice(0, maxLength)
    const lastSpace = title.lastIndexOf(' ')
    if (lastSpace > maxLength * 0.6) {
      title = title.slice(0, lastSpace)
    }
    title = title.trim() + '...'
  }

  return title
}

/**
 * Parse the market demand evaluation reasoning to extract structured sections
 * Market demand sections: "What's Promising", "What's Uncertain", "How to Validate"
 */
export function parseMarketDemandSections(reasoning) {
  if (!reasoning) {
    console.log('parseMarketDemandSections: no reasoning provided')
    return null
  }

  console.log('parseMarketDemandSections: parsing market demand reasoning of length', reasoning.length)

  // Extract market demand sections - handle markdown format with ** wrapper
  let promisingMatch = reasoning.match(/\*\*(?:✨\s*)?What's Promising:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|❓|🔍|$)/i)
  let uncertainMatch = reasoning.match(/\*\*(?:❓\s*)?What's Uncertain:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|🔍|$)/i)
  let validateMatch = reasoning.match(/\*\*(?:🔍\s*)?How to Validate:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|$)/i)

  // Try plain text format (emoji on own line or inline, no ** wrapper)
  if (!promisingMatch) {
    promisingMatch = reasoning.match(/✨\s*\n?\s*What's Promising\s*\n([\s\S]*?)(?=\n\n?❓|$)/i)
  }
  if (!uncertainMatch) {
    uncertainMatch = reasoning.match(/❓\s*\n?\s*What's Uncertain\s*\n([\s\S]*?)(?=\n\n?🔍|$)/i)
  }
  if (!validateMatch) {
    validateMatch = reasoning.match(/🔍\s*\n?\s*How to Validate\s*\n([\s\S]*?)$/i)
  }

  console.log('parseMarketDemandSections: matches found:', {
    promising: !!promisingMatch,
    uncertain: !!uncertainMatch,
    validate: !!validateMatch
  })

  const parseSection = (content) => {
    if (!content) return null
    const lines = content.trim().split('\n').filter(line => line.trim())
    // Match bullets starting with -, •, ▸, or numbered items
    const bulletPattern = /^[-•▸]\s*|^\d+\.\s*/
    const bullets = lines.filter(line => bulletPattern.test(line.trim()))
    const summaryLines = lines.filter(line => !bulletPattern.test(line.trim()))
    return {
      summary: summaryLines.join(' ').trim(),
      bullets: bullets.map(b => b.trim().replace(/^[-•▸]\s*/, '').replace(/^\d+\.\s*/, ''))
    }
  }

  const result = {
    promising: parseSection(promisingMatch?.[1]),
    uncertain: parseSection(uncertainMatch?.[1]),
    validate: parseSection(validateMatch?.[1])
  }

  console.log('parseMarketDemandSections: result:', {
    hasPromising: !!result.promising,
    promisingBullets: result.promising?.bullets?.length || 0,
    hasUncertain: !!result.uncertain,
    uncertainBullets: result.uncertain?.bullets?.length || 0,
    hasValidate: !!result.validate,
    validateBullets: result.validate?.bullets?.length || 0
  })

  return result
}

/**
 * Parse the USP evaluation reasoning to extract structured sections
 * USP sections: "What Makes This Different", "Where Differentiation is Weak", "How to Strengthen Uniqueness"
 */
export function parseUSPSections(reasoning) {
  if (!reasoning) {
    console.log('parseUSPSections: no reasoning provided')
    return null
  }

  console.log('parseUSPSections: parsing USP reasoning of length', reasoning.length)

  // Extract USP sections - handle markdown format with ** wrapper
  let differentiatorMatch = reasoning.match(/\*\*(?:💎\s*)?What Makes This Different:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|❓|✨|$)/i)
  let weakDiffMatch = reasoning.match(/\*\*(?:❓\s*)?Where Differentiation is Weak:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|✨|$)/i)
  let strengthenMatch = reasoning.match(/\*\*(?:✨\s*)?How to Strengthen Uniqueness:?\*\*\s*([\s\S]*?)(?=\n\n\*\*|$)/i)

  // Try plain text format (emoji on own line or inline, no ** wrapper)
  if (!differentiatorMatch) {
    differentiatorMatch = reasoning.match(/💎\s*\n?\s*What Makes This Different\s*\n([\s\S]*?)(?=\n\n?❓|$)/i)
  }
  if (!weakDiffMatch) {
    weakDiffMatch = reasoning.match(/❓\s*\n?\s*Where Differentiation is Weak\s*\n([\s\S]*?)(?=\n\n?✨|$)/i)
  }
  if (!strengthenMatch) {
    strengthenMatch = reasoning.match(/✨\s*\n?\s*How to Strengthen Uniqueness\s*\n([\s\S]*?)$/i)
  }

  console.log('parseUSPSections: matches found:', {
    differentiators: !!differentiatorMatch,
    weakDiff: !!weakDiffMatch,
    strengthen: !!strengthenMatch
  })

  const parseSection = (content) => {
    if (!content) return null
    const lines = content.trim().split('\n').filter(line => line.trim())
    // Match bullets starting with -, •, ▸, or numbered items
    const bulletPattern = /^[-•▸]\s*|^\d+\.\s*/
    const bullets = lines.filter(line => bulletPattern.test(line.trim()))
    const summaryLines = lines.filter(line => !bulletPattern.test(line.trim()))
    return {
      summary: summaryLines.join(' ').trim(),
      bullets: bullets.map(b => b.trim().replace(/^[-•▸]\s*/, '').replace(/^\d+\.\s*/, ''))
    }
  }

  const result = {
    differentiators: parseSection(differentiatorMatch?.[1]),
    weakDifferentiation: parseSection(weakDiffMatch?.[1]),
    strengthenUniqueness: parseSection(strengthenMatch?.[1])
  }

  console.log('parseUSPSections: result:', {
    hasDifferentiators: !!result.differentiators,
    differentiatorBullets: result.differentiators?.bullets?.length || 0,
    hasWeakDiff: !!result.weakDifferentiation,
    weakDiffBullets: result.weakDifferentiation?.bullets?.length || 0,
    hasStrengthen: !!result.strengthenUniqueness,
    strengthenBullets: result.strengthenUniqueness?.bullets?.length || 0
  })

  return result
}

/**
 * Extract tasks from the Next Steps section
 */
export function extractTasksFromNextSteps(sections) {
  if (!sections?.nextSteps?.bullets?.length) {
    return []
  }

  return sections.nextSteps.bullets.map((bullet, idx) => ({
    title: generateTaskTitle(bullet),
    description: bullet, // Full text as description
    source: 'orin'
  }))
}

/**
 * Check if Orin insights have already been generated for this idea
 * by checking if tasks with source 'orin' exist
 */
export function hasOrinInsightsBeenGenerated(tasks) {
  if (!tasks?.length) return false
  return tasks.some(task => task.source === 'orin' || task.source === 'orin_assessment')
}

/**
 * Check if whiteboard has SWOT content
 */
export function hasSwotWhiteboardContent(whiteboardData) {
  if (!whiteboardData?.store) return false

  const shapes = Object.values(whiteboardData.store).filter(item => item.typeName === 'shape')
  // Check if there are frame shapes with SWOT-related names
  const hasSwotFrames = shapes.some(shape =>
    shape.type === 'frame' &&
    (shape.props?.name?.includes('Strengths') ||
     shape.props?.name?.includes('Challenges') ||
     shape.props?.name?.includes('Next Steps'))
  )

  return hasSwotFrames
}
